import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import { getExpoStatistics, getDetailedAnalysis } from "../Apis/DashboardAPIs";
import { getExposList } from "../Apis/ExpoAPIs";

const DashboardCharts = () => {
  const [selectedExpo, setSelectedExpo] = useState(null);
  const [expoStatistics, setExpoStatistics] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);

  const [expoList, setExpoList] = useState([]);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const [selectedYear, setSelectedYear] = useState({ value: 2026, label: "2026" });
  const [selectedMonth, setSelectedMonth] = useState({ value: 7, label: "July" });

  const monthOptions = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" },
  ];

  const yearOptions = [
    { value: 2024, label: "2024" }, { value: 2025, label: "2025" },
    { value: 2026, label: "2026" }, { value: 2027, label: "2027" },
  ];

  const fetchExpoStats = async () => {
    setStatsLoading(true);
    try {
      const res = await getExpoStatistics({ year: selectedYear.value, month: selectedMonth.value });
      if (res.status) {
        setExpoStatistics(res.response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching expo stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchExpoList = async () => {
    try {
      const res = await getExposList();
      if (res.status) {
        const list = res.response?.data || [];
        const options = list.map((e) => ({ value: e._id, label: e.expoName }));
        setExpoList(options);
        if (options.length > 0 && !selectedExpo) setSelectedExpo(options[0]);
      }
    } catch (error) {
      console.error("Error fetching expo list:", error);
    }
  };

  const fetchDetailedAnalysis = async (expoId) => {
    setAnalysisLoading(true);
    try {
      const res = await getDetailedAnalysis(expoId);
      if (res.status) {
        const data = res.response?.data;
        const pipelineStatus = data?.pipelineStatus || [];
        
        let labels = [];
        let series = [];
        pipelineStatus.forEach(status => {
          labels.push(status._id || 'Unknown');
          series.push(status.count || 0);
        });

        if(labels.length === 0) {
            labels = ["No Data"];
            series = [0];
        }

        setAnalysisData({
          labels,
          series,
          revenue: data?.revenue || 0
        });
      }
    } catch (error) {
      console.error("Error fetching detailed analysis:", error);
    } finally {
      setAnalysisLoading(false);
    }
  };

  useEffect(() => {
    fetchExpoStats();
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchExpoList();
  }, []);

  useEffect(() => {
    if (selectedExpo?.value) {
      fetchDetailedAnalysis(selectedExpo.value);
    }
  }, [selectedExpo]);

  const bookingChartOptions = {
    chart: { type: "bar", toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
    colors: ["#ed1c24", "#334155"],
    plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: {
      categories: (expoStatistics || []).map((item) => item?.expoName || ""),
      axisBorder: { show: false },
      labels: { style: { fontSize: "11px", fontWeight: 600, colors: ['#64748b'] } },
    },
    yaxis: {
      labels: { style: { fontSize: "11px", fontWeight: 600, colors: ['#64748b'] } },
    },
    fill: { opacity: 1 },
    grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
    legend: { position: "top", horizontalAlign: "right", fontWeight: 600 },
    tooltip: { theme: "light" }
  };

  const bookingChartSeries = [
    { name: "Stalls Booked", data: (expoStatistics || []).map((item) => item?.stallBookedCount || 0) },
    { name: "Visitors Registered", data: (expoStatistics || []).map((item) => item?.visitorBookedCount || 0) },
  ];

  const donutOptions = {
    chart: { type: "donut", fontFamily: 'Inter, sans-serif' },
    labels: analysisData?.labels || [],
    colors: ["#28a745", "#f59e0b", "#3b82f6", "#ef4444", "#8b5cf6", "#64748b"],
    legend: { position: "bottom", fontSize: '11px', fontWeight: 500, markers: { width: 8, height: 8 }, itemMargin: { horizontal: 8, vertical: 2 } },
    plotOptions: { 
        pie: { 
            donut: { 
                size: "70%",
                labels: {
                    show: true,
                    name: { show: true, fontSize: '12px', fontWeight: 600, color: '#64748b' },
                    value: { show: true, fontSize: '24px', fontWeight: 700, color: '#0f172a' },
                    total: {
                        show: true,
                        showAlways: true,
                        label: 'Total Clients',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#64748b',
                    }
                }
            } 
        } 
    },
    dataLabels: { enabled: false },
    stroke: { show: true, colors: ['#ffffff'], width: 2 },
    tooltip: { theme: 'light' }
  };

  return (
    <div className="d-flex flex-column gap-24">
      {/* Top Level Row: Expo Stats & Pipeline Health */}
      <div className="row gy-4 mb-24">
        {/* Expo Performance Bar Chart */}
        <div className="col-xl-8 col-lg-12">
          <div className="card h-100 shadow-sm border-0 radius-12 overflow-hidden">
            <div className="card-header border-bottom py-16 px-24 d-flex flex-wrap align-items-center justify-content-between gap-3 bg-white">
              <div className="d-flex align-items-center gap-2">
                <div className="bg-danger-focus text-danger-main d-flex justify-content-center align-items-center rounded-circle" style={{ width: "32px", height: "32px" }}>
                    <Icon icon="solar:chart-square-bold" className="text-lg" />
                </div>
                <h6 className="mb-0 text-lg fw-bold text-dark">Monthly Expos Performance</h6>
              </div>
              <div className="d-flex gap-2">
                <div style={{ width: "130px" }}>
                  <Select options={monthOptions} value={selectedMonth} onChange={setSelectedMonth} styles={selectStyles({ compact: true })} isSearchable={false} />
                </div>
                <div style={{ width: "100px" }}>
                  <Select options={yearOptions} value={selectedYear} onChange={setSelectedYear} styles={selectStyles({ compact: true })} isSearchable={false} />
                </div>
              </div>
            </div>
            <div className="card-body p-24">
              {statsLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
                  <div className="spinner-border text-danger" role="status"></div>
                </div>
              ) : expoStatistics.length > 0 ? (
                <ReactApexChart options={bookingChartOptions} series={bookingChartSeries} type="bar" height={320} />
              ) : (
                <div className="d-flex flex-column justify-content-center align-items-center text-secondary-light" style={{ height: "320px" }}>
                    <Icon icon="solar:calendar-broken" className="display-4 mb-2 opacity-50" />
                    <p className="fw-medium">No expos found for this month.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pipeline Health Donut Chart & Revenue */}
        <div className="col-xl-4 col-lg-12">
          <div className="card h-100 shadow-sm border-0 radius-12 overflow-hidden" style={{ background: "linear-gradient(180deg, #fff, #f8fafc)" }}>
            <div className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between bg-white">
                <div className="d-flex align-items-center gap-2">
                    <div className="bg-primary-focus text-primary-main d-flex justify-content-center align-items-center rounded-circle" style={{ width: "32px", height: "32px" }}>
                        <Icon icon="solar:pie-chart-2-bold" className="text-lg" />
                    </div>
                    <h6 className="mb-0 text-lg fw-bold text-dark">Pipeline Health</h6>
                </div>
            </div>
            <div className="card-body p-24 d-flex flex-column">
              <div className="mb-4">
                <Select value={selectedExpo} onChange={setSelectedExpo} options={expoList} styles={selectStyles({ compact: true })} placeholder="Select an Expo..." />
              </div>
              
              <div className="flex-grow-1 d-flex flex-column justify-content-center">
                {analysisLoading ? (
                  <div className="text-center py-40">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : analysisData ? (
                  <>
                    <ReactApexChart options={donutOptions} series={analysisData.series} type="donut" height={280} />
                    
                    <div className="mt-4 p-16 radius-8 text-center" style={{ background: "linear-gradient(135deg, #000 0%, #bb0007 50%, #ed1c24 100%)" }}>
                        <span className="text-white opacity-75 text-xs text-uppercase fw-semibold letter-spacing-1 d-block mb-1">Generated Revenue</span>
                        <h3 className="fw-bold text-white mb-0" style={{ whiteSpace: "nowrap", fontSize: "clamp(1.2rem, 2.5vw, 2rem)" }}>₹{analysisData.revenue?.toLocaleString('en-IN') || 0}</h3>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-secondary-light py-40">
                    <p className="fw-medium mb-0">Please select an expo.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCharts;
